import { DownloadButton, Icon } from '@anytag/shared-atoms';
import { GoogleAuthDialog, useGoogleAuthDownload } from '@anytag/shared-molecules';
import { useUserRoles } from '@anytag/auth';
import { cn, getRestApiUrl } from '@anytag/shared-utils';
import { GTM_IDS, THEME } from '@anytag/shared-constants';
import { CustomDownloadType, DownloadFileType, DropdownOption, MenuTypeLevel } from '@anytag/shared-types';
import { useDownloadFile, useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import {
  CampaignType,
  useMarketplaceGoogleSlidesHistoryQuery,
  useRequestPowerpointCustomisedReportMutation,
  useRequestGoogleSlideCustomisedReportMutation,
  useEngagementPostsMissingMetricsForReportQuery
} from '@anytag/graphql';
import { usePostReportContext } from '../PostReportContext';
import { PostReportSegmentKeys, PostReportTableStatKeys } from '../PostReportTable';
import { PostReportFilterFormValues, PostReportSearchSchemaType, usePostReportFilter } from '../PostReportFilter';
import { GoogleSlidesHistoryModal } from './GoogleSlidesHistoryModal';

type PostDownloadOption = DropdownOption<DownloadFileType | CustomDownloadType>;

export interface DownloadPostReportsButtonProps {
  className?: string;
  campaignId: number;
  filter: PostReportFilterFormValues;
  segmentState: PostReportSearchSchemaType['state'];
  defaultColumnsForDownload?: PostReportTableStatKeys[];
}

export const DownloadPostReportsButton = ({
  filter,
  className,
  campaignId,
  segmentState,
  defaultColumnsForDownload = []
}: DownloadPostReportsButtonProps) => {
  const ggSlidesModal = useToggleState();
  const { t, enqueueSnackbar, search } = useQueryHelper();
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { campaignType, tableRef } = usePostReportContext();
  const { sort } = usePostReportFilter();
  const { isAdminStaff, isAdvertiser, isAdminStaffAgencyTalentAgencyPartner, isPartnerFromUuum } = useUserRoles();
  const { callRequestPowerpointCustomisedReport, loading: requestingPptxReport } =
    useRequestPowerpointCustomisedReportMutation();
  const { callRequestGoogleSlideCustomisedReport, loading: requestingGgSlideReport } =
    useRequestGoogleSlideCustomisedReportMutation();
  const { data } = useMarketplaceGoogleSlidesHistoryQuery({
    skip: campaignType !== CampaignType.MARKETPLACE || !isAdminStaff,
    variables: { campaignId }
  });
  const { dialogProps, handleEngagementGoogleDocsDownload } = useGoogleAuthDownload({
    goBackUrl: '/engagement/$id/report',
    engagementId: campaignId,
    searchParams: search
  });

  const hideGoogleDocument = !isAdminStaff || campaignType === CampaignType.MARKETPLACE;
  const { data: engagementPostsMissingMetricsForReportData } = useEngagementPostsMissingMetricsForReportQuery({
    variables: { campaignId },
    skip: hideGoogleDocument
  });

  const hasMissingMetrics =
    !!engagementPostsMissingMetricsForReportData?.engagementPostsMissingMetricsForReport?.ids?.length;

  const ggSlidesHistory =
    data?.marketplaceGoogleSlidesHistory?.map((history) => ({ date: history.date, url: history.slideUrl })) || [];
  const hasGgSlidesHistory = isAdminStaff && campaignType === CampaignType.MARKETPLACE && !!ggSlidesHistory.length;
  const campaignText = campaignType === CampaignType.ENGAGEMENT ? 'engagement' : 'marketplace';
  const options: PostDownloadOption[] = [
    {
      label: t<string>('CSV'),
      value: DownloadFileType.CSV,
      gtmId: GTM_IDS[campaignText].report_csv_download
    },
    {
      label: t('Google Slide'),
      value: DownloadFileType.GOOGLE_SLIDE,
      gtmId: GTM_IDS[campaignText].report_google_slide_download,
      hidden: !isAdminStaff && !isPartnerFromUuum
    },
    {
      label: t('Powerpoint'),
      value: DownloadFileType.PPTX,
      gtmId: GTM_IDS[campaignText].report_pptx_download,
      hidden: !(isAdvertiser || isAdminStaffAgencyTalentAgencyPartner)
    },
    {
      value: CustomDownloadType.GG_SLIDE_HISTORY,
      hidden: !hasGgSlidesHistory,
      label: (
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <Icon icon="cloud" size={12} variant="inherit" className="mr-1" />
          {t('Google Slides previously exported')}
        </div>
      ),
      css: { backgroundColor: THEME.background.colors.gray.lv1, borderTop: THEME.border.base }
    },
    {
      label: (
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <span>{t('Google Document')}</span>
          {hasMissingMetrics ? (
            <Icon icon="warning" size={16} variant="inherit" className="ml-1 text-attention-yellow" />
          ) : null}
        </div>
      ),
      value: CustomDownloadType.GOOGLE_DOCS_VIDEO,
      hidden: hideGoogleDocument,
      menuType: MenuTypeLevel.MULTI_LEVEL,
      className: cn('box-border min-w-40 p-2'),
      submenu: [
        {
          label: t('Video'),
          value: CustomDownloadType.GOOGLE_DOCS_VIDEO
        },
        {
          label: t('Static Post'),
          value: CustomDownloadType.GOOGLE_DOCS_STATIC_POST
        }
      ]
    }
  ].filter((option) => !option.hidden);

  const handleDownloadPostReport = async (fileType?: PostDownloadOption['value']) => {
    if (fileType === CustomDownloadType.GG_SLIDE_HISTORY) {
      if (hasGgSlidesHistory) {
        ggSlidesModal.open();
      }

      return;
    } else if (
      !fileType ||
      ![
        DownloadFileType.CSV,
        DownloadFileType.GOOGLE_SLIDE,
        DownloadFileType.PPTX,
        CustomDownloadType.GOOGLE_DOCS_VIDEO,
        CustomDownloadType.GOOGLE_DOCS_STATIC_POST
      ].includes(fileType)
    ) {
      return;
    }

    try {
      const visibleColumns = tableRef?.current?.getVisibleFlatColumns() || [];
      const selectedColumns = visibleColumns.reduce<PostReportTableStatKeys[]>((results, curr) => {
        if (curr.columnDef.meta?.sortField && curr.id) {
          results.push(curr.id as PostReportTableStatKeys);
        }

        return results;
      }, defaultColumnsForDownload);
      const variables = { campaignId, campaignType, selectedMetrics: selectedColumns };

      switch (fileType) {
        case DownloadFileType.CSV:
          {
            const unsupportedCSVDownloadColumns: PostReportTableStatKeys[] = [
              'malePercentage',
              'femalePercentage',
              'age1824Percentage',
              'age2534Percentage',
              'age35UpPercentage'
            ];

            await handleDownloadFile({
              filenamePrefix: 'report_post',
              url: getRestApiUrl(`report/campaign/${campaignType.valueOf().toLowerCase()}/${campaignId}/report/csv/`),
              params: {
                ...sort,
                keyword: filter.keyword,
                endDate: filter.endDate,
                startDate: filter.startDate,
                selectedColumns: selectedColumns.filter((key) => !unsupportedCSVDownloadColumns.includes(key)),
                groups: (Object.keys(segmentState) as PostReportSegmentKeys[])
                  .reduce<PostReportSegmentKeys[]>((acc, curr) => (segmentState[curr] ? [...acc, curr] : acc), [])
                  .join()
              }
            });
          }
          break;
        case DownloadFileType.GOOGLE_SLIDE:
          await callRequestGoogleSlideCustomisedReport({ variables });
          enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
          break;
        case DownloadFileType.PPTX:
          await callRequestPowerpointCustomisedReport({ variables });
          enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
          break;
        case CustomDownloadType.GOOGLE_DOCS_VIDEO:
          await handleEngagementGoogleDocsDownload(true);
          break;
        case CustomDownloadType.GOOGLE_DOCS_STATIC_POST:
          await handleEngagementGoogleDocsDownload(false);
          break;
        default:
          break;
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <DownloadButton<PostDownloadOption>
        options={options}
        className={cn('min-w-49.5', className)}
        onSelectOption={({ value }) => handleDownloadPostReport(value)}
        loading={downloading || requestingPptxReport || requestingGgSlideReport}
      />
      <GoogleAuthDialog {...dialogProps} />
      {hasGgSlidesHistory ? (
        <GoogleSlidesHistoryModal open={ggSlidesModal.status} onClose={ggSlidesModal.close} data={ggSlidesHistory} />
      ) : null}
    </>
  );
};
